import { createTheme, ThemeOptions } from '@mui/material/styles';
import { CURRENT_BRANDING } from '../config/brandingConfig';

// Brand Colors (from tenant configuration)
export const ontarioTechColors = {
    primary: {
        main: CURRENT_BRANDING.colors.primary,
        light: CURRENT_BRANDING.colors.primaryLight,
        dark: CURRENT_BRANDING.colors.primaryDark,
        contrastText: '#FFFFFF',
  },
    secondary: {
        main: CURRENT_BRANDING.colors.secondary,
        light: CURRENT_BRANDING.colors.secondaryLight,
        dark: CURRENT_BRANDING.colors.secondaryDark,
        contrastText: '#FFFFFF',
  },
  background: {
    default: '#FFFFFF',
    paper: '#F8F9FA',
  },
  text: {
    primary: '#2C507A',
    secondary: '#6C757D',
  },
  grey: {
    50: '#F8F9FA',
    100: '#E9ECEF',
    200: '#DEE2E6',
    300: '#CED4DA',
    400: '#ADB5BD',
    500: '#6C757D',
    600: '#495057',
    700: '#343A40',
    800: '#212529',
    900: '#000000',
  },
  success: {
    main: '#F06C38', // Using orange for success states
  },
  error: {
    main: '#DC3545',
  },
  warning: {
    main: '#FFC107',
  },
  info: {
    main: '#2C507A',
  },
};

// Typography configuration
export const ontarioTechTypography = {
  fontFamily: '"Arial", "Helvetica", sans-serif',
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: ontarioTechColors.text.primary,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    color: ontarioTechColors.text.primary,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    color: ontarioTechColors.text.primary,
    lineHeight: 1.3,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: ontarioTechColors.text.primary,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: ontarioTechColors.text.primary,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: ontarioTechColors.text.primary,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    color: ontarioTechColors.grey[600],
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: ontarioTechColors.text.secondary,
    lineHeight: 1.5,
  },
  button: {
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
};

// Component styling overrides
export const ontarioTechComponents: ThemeOptions['components'] = {
  MuiButton: {
    styleOverrides: {
      root: {
        fontSize: '1rem',
        fontWeight: 600,
        padding: '12px 24px',
        minHeight: '48px',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        borderRadius: '4px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(44, 80, 122, 0.2)',
        },
      },
      contained: {
        backgroundColor: ontarioTechColors.primary.main,
        '&:hover': {
          backgroundColor: ontarioTechColors.primary.dark,
        },
      },
      outlined: {
        borderColor: ontarioTechColors.primary.main,
        color: ontarioTechColors.primary.main,
        '&:hover': {
          backgroundColor: ontarioTechColors.background.paper,
          borderColor: ontarioTechColors.primary.dark,
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: ontarioTechColors.primary.main,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #E9ECEF',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
      },
      colorPrimary: {
        backgroundColor: ontarioTechColors.primary.main,
        color: ontarioTechColors.primary.contrastText,
      },
      colorSecondary: {
        backgroundColor: ontarioTechColors.secondary.main,
        color: ontarioTechColors.secondary.contrastText,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: ontarioTechColors.grey[300],
          },
          '&:hover fieldset': {
            borderColor: ontarioTechColors.primary.main,
          },
          '&.Mui-focused fieldset': {
            borderColor: ontarioTechColors.primary.main,
          },
        },
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        color: ontarioTechColors.primary.main,
        '&.Mui-checked': {
          color: ontarioTechColors.primary.main,
        },
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: ontarioTechColors.grey[200],
      },
    },
  },
};

// Create the complete theme
export const createOntarioTechTheme = () => {
  return createTheme({
    palette: ontarioTechColors,
    typography: ontarioTechTypography,
    components: ontarioTechComponents,
  });
};

// Utility functions for consistent styling
export const ontarioTechStyles = {
  
  // Section headers
  sectionTitle: {
    color: ontarioTechColors.text.primary,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  
  // Button styles
  primaryButton: {
    backgroundColor: ontarioTechColors.primary.main,
    '&:hover': {
      backgroundColor: ontarioTechColors.primary.dark,
    },
  },
  secondaryButton: {
    borderColor: ontarioTechColors.primary.main,
    color: ontarioTechColors.primary.main,
    '&:hover': {
      backgroundColor: ontarioTechColors.background.paper,
      borderColor: ontarioTechColors.primary.dark,
    },
  },
  
  // Text styles
  primaryText: {
    color: ontarioTechColors.text.primary,
  },
  secondaryText: {
    color: ontarioTechColors.text.secondary,
  },
  successText: {
    color: ontarioTechColors.secondary.main,
  },
  
  // Card styles
  summaryCard: {
    backgroundColor: ontarioTechColors.background.paper,
    border: '2px solid #E9ECEF',
  },
  
  // Icon styles
  primaryIcon: {
    color: ontarioTechColors.primary.main,
  },
  secondaryIcon: {
    color: ontarioTechColors.secondary.main,
  },
  
  // Logo styles
  logoContainer: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: ontarioTechColors.primary.main,
    border: '3px solid #F06C38',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 2,
  },
  logoText: {
    color: ontarioTechColors.secondary.main,
    fontWeight: 700,
    fontSize: '1.2rem',
    lineHeight: 1,
  },

  // Header styles
  header: {
    backgroundColor: ontarioTechColors.primary.main,
    color: ontarioTechColors.primary.contrastText,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    textAlign: 'center' as const,
    mb: 3,
    p: 3,
    borderRadius: 2,
  },
  headerTitle: {
    color: ontarioTechColors.primary.contrastText,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    mb: 1,
  },
  headerSubtitle: {
    color: ontarioTechColors.grey[100],
    fontWeight: 400,
    fontSize: '1.1rem',
  },

  // Button styles
  button: {
    primary: {
      backgroundColor: ontarioTechColors.primary.main,
      color: ontarioTechColors.primary.contrastText,
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      boxShadow: '0 2px 8px rgba(44, 80, 122, 0.3)',
      minWidth: 200,
      py: 1.5,
      px: 3,
      '&:hover': {
        backgroundColor: ontarioTechColors.primary.dark,
        boxShadow: '0 4px 12px rgba(44, 80, 122, 0.4)',
      },
      '&:disabled': {
        backgroundColor: ontarioTechColors.grey[400],
        color: ontarioTechColors.primary.contrastText,
      },
    },
    secondary: {
      borderColor: ontarioTechColors.primary.main,
      color: ontarioTechColors.primary.main,
      backgroundColor: ontarioTechColors.background.default,
      borderWidth: 2,
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      minWidth: 150,
      py: 1.5,
      px: 3,
      '&:hover': {
        backgroundColor: ontarioTechColors.primary.main,
        color: ontarioTechColors.primary.contrastText,
        borderColor: ontarioTechColors.primary.main,
        borderWidth: 2,
      },
    },
    cart: {
      ml: 2,
      backgroundColor: 'rgba(255,255,255,0.15)',
      border: '2px solid rgba(255,255,255,0.3)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.25)',
        border: '2px solid rgba(255,255,255,0.5)',
        transform: 'scale(1.05)',
      },
      transition: 'all 0.2s ease-in-out',
    },
  },

  // Card styles
  card: {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    borderRadius: 2,
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    p: 4,
  },

  // TextField styles
  textField: {
    maxWidth: 400,
    '& .MuiOutlinedInput-root': {
      fontSize: '1.2rem',
      padding: '12px',
    },
  },

  // Scanner area styles
  scannerArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
  },
  scannerIcon: {
    fontSize: 80,
    color: ontarioTechColors.secondary.main,
    mb: 2,
  },
  scannerTitle: {
    color: ontarioTechColors.primary.main,
    fontWeight: 600,
    textAlign: 'center' as const,
    mb: 2,
  },

  // Product found styles
  productFound: {
    p: 3,
    textAlign: 'center' as const,
  },
  productFoundTitle: {
    color: ontarioTechColors.secondary.main,
    fontWeight: 700,
    mb: 2,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  productName: {
    color: ontarioTechColors.primary.main,
    fontWeight: 600,
    mb: 1,
  },
  productPrice: {
    color: ontarioTechColors.primary.main,
    fontWeight: 700,
    mb: 2,
  },
  productAuthor: {
    color: ontarioTechColors.grey[500],
    mb: 2,
    fontSize: '1rem',
  },
  productAdding: {
    color: ontarioTechColors.grey[500],
    mb: 3,
    fontSize: '0.9rem',
  },

  // Action buttons container
  actionButtons: {
    display: 'flex',
    gap: 2,
    justifyContent: 'center',
    backgroundColor: ontarioTechColors.grey[50],
    p: 3,
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },

  // Cart badge styles
  cartBadge: {
    position: 'absolute' as const,
    top: 6,
    right: 6,
    backgroundColor: ontarioTechColors.secondary.main,
    color: ontarioTechColors.primary.contrastText,
    borderRadius: '50%',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: 700,
    border: `2px solid ${ontarioTechColors.primary.contrastText}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },

  // Main container styles
  mainContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    p: 2,
    gap: 2,
  },
  appContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'visible',
    backgroundColor: '#f5f5f5',
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    p: 2,
    overflow: 'auto',
  },

  // Header text styles
  headerText: {
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '1.4rem',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  headerSubtext: {
    color: '#E9ECEF',
    fontSize: '1rem',
    fontWeight: 500,
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },

  // Contrast styles for dark backgrounds
  contrast: {
    // White text for dark backgrounds
    whiteText: {
      color: '#FFFFFF',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '1px',
    },
    
    // Semi-transparent white buttons for dark backgrounds
    whiteButton: {
      color: '#FFFFFF',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.5)',
      },
    },
    
    // Semi-transparent white icon buttons for dark backgrounds
    whiteIconButton: {
      color: '#FFFFFF',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },
    },
    
    // Semi-transparent white chip for dark backgrounds
    whiteChip: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: '#FFFFFF',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      '& .MuiChip-icon': {
        color: '#FFFFFF',
      },
    },
  },

  // Receipt styles
  receipt: {
    // Thank you message styling
    thankYouMessage: {
      color: '#FFFFFF',
      fontWeight: 600,
      fontSize: '1.1rem',
      textAlign: 'center' as const,
    },
    
    // Action buttons container
    actionButtons: {
      display: 'flex',
      gap: 2,
      justifyContent: 'center',
      backgroundColor: ontarioTechColors.grey[50],
      p: 3,
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    
    // Primary action button (New Transaction)
    primaryActionButton: {
      backgroundColor: ontarioTechColors.primary.main,
      color: ontarioTechColors.primary.contrastText,
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      minWidth: 150,
      py: 1.5,
      px: 3,
      boxShadow: '0 2px 8px rgba(44, 80, 122, 0.3)',
      '&:hover': {
        backgroundColor: ontarioTechColors.primary.dark,
        boxShadow: '0 4px 12px rgba(44, 80, 122, 0.4)',
      },
    },
    
    // Secondary action buttons (Print, Email, Download)
    secondaryActionButton: {
      borderColor: ontarioTechColors.primary.main,
      color: ontarioTechColors.primary.main,
      backgroundColor: ontarioTechColors.background.default,
      borderWidth: 2,
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      minWidth: 120,
      py: 1.5,
      px: 3,
      '&:hover': {
        backgroundColor: ontarioTechColors.grey[50],
        borderColor: ontarioTechColors.primary.dark,
      },
    },
  },
};
